import { useLayoutEffect, useSyncExternalStore, type RefObject } from "react";
import type { WindowOrder } from "./order.ts";
import type { WindowId } from "./places.ts";

/**
 * A play window's part in the windows' order (P2.8-T16; ADR-061's amendment "a window can be moved", item 8): a press
 * anywhere on the window brings it to the front, and the window hears whether it is in front, which its styles draw
 * it by. The press is heard as it goes down and is neither prevented nor stopped, so the control under it still
 * takes it and a drag of the title bar still starts.
 */

/**
 * A source that never names a front window, for a page with one play window.
 */
const ALONE = { subscribe: () => () => undefined, getSnapshot: () => null } as const;

/**
 * Raises a window on a press and says whether it is in front.
 *
 * @param host The window's box.
 * @param order The play windows' order, or undefined on a page with one play window.
 * @param id The window.
 * @returns True while the window is the one in front.
 * @example
 * const front = useRaise(host, order, "chest");
 */
export function useRaise(host: RefObject<HTMLElement | null>, order: WindowOrder | undefined, id: WindowId): boolean {
  const source = order ?? ALONE;
  const top = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  useLayoutEffect(() => {
    const box = host.current;
    if (box === null || order === undefined) {
      return undefined;
    }
    /**
     * Brings the window to the front.
     */
    const pressed = (): void => order.raise(id);
    box.addEventListener("pointerdown", pressed, true);
    return () => box.removeEventListener("pointerdown", pressed, true);
  }, [host, order, id]);
  return top === id;
}
